"use client";

import { useState, useMemo } from "react";
import { TrackWithProxy } from "@/lib/types";
import TrackRow from "./TrackRow";

interface ChartListProps {
  tracks: TrackWithProxy[];
}

export default function ChartList({ tracks }: ChartListProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return tracks;
    const q = query.toLowerCase().trim();
    return tracks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.artist.toLowerCase().includes(q)
    );
  }, [tracks, query]);

  return (
    <>
      {/* Search */}
      <div className="relative mb-5">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-white/30"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по названию или артисту..."
          className="w-full rounded-xl border border-white/[0.06] bg-white/[0.03] py-3 pl-11 pr-10 text-sm text-white placeholder-white/30 outline-none transition-all focus:border-violet-500/40 focus:bg-white/[0.05] focus:ring-1 focus:ring-violet-500/20"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-white/30 transition-colors hover:text-white/60"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Results info when searching */}
      {query.trim() && (
        <div className="mb-3 px-4 text-xs text-white/30">
          {filtered.length > 0
            ? `Найдено: ${filtered.length}`
            : "Ничего не найдено"}
        </div>
      )}

      {/* Chart Legend */}
      <div className="mb-4 flex items-center gap-4 border-b border-white/5 px-4 pb-3 text-[10px] font-medium uppercase tracking-widest text-white/25">
        <span className="w-8 text-center sm:w-12">#</span>
        <span className="w-11 sm:w-12"></span>
        <span className="flex-1">Трек</span>
        <span className="hidden w-16 text-center lg:block">Пик</span>
        <span className="hidden w-16 text-center lg:block">Дней</span>
        <span className="w-8"></span>
      </div>

      {/* Track List */}
      <div className="flex flex-col gap-0.5">
        {filtered.map((track, index) => (
          <div
            key={`${track.id}-${track.position}`}
            className="animate-fade-in-up"
            style={{
              animationDelay: query
                ? "0ms"
                : `${Math.min(index * 15, 600)}ms`,
            }}
          >
            <TrackRow track={track} />
          </div>
        ))}
      </div>
    </>
  );
}
